import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/client/react';
import { useFormik } from 'formik';
import { format, startOfYear } from 'date-fns';
import { Input, SelectText } from '@airtm/airtm-ui';
import SelectCountry from 'components/SelectCountry';
import SelectCountryPhoneCode from 'components/SelectCountryPhoneCode/SelectCountryPhoneCode';
import SelectBirthDate from 'components/SelectBirthDate';
import StatesQuery from 'graphqlDocuments/queries/States.graphql';
import PhoneVerification from 'components/PhoneVerification/PhoneVerification';
import { useTranslation } from 'react-i18next';
import CompleteProfileFormSchema from './CompleteProfileFormSchema';

const getTouchedObj = (errors) => {
  const touched = {};
  Object.keys(errors).forEach((key) => {
    if (Array.isArray(errors[key])) {
      errors[key].forEach((val, index) => {
        if (index === 0) {
          touched[key] = [];
        }
        touched[key].push(getTouchedObj(val));
      });
    } else {
      touched[key] = true;
    }
  });

  return touched;
};

function CompleteProfileForm({ id, user, onSubmit, serverErrors, submitting }) {
  const { t } = useTranslation(['ERRORS', 'SIGNUP', 'FORM_FIELDS', 'STATES']);

  const formik = useFormik({
    validationSchema: CompleteProfileFormSchema,
    initialValues: {
      addressLine1: '',
      addressLine2: '',
      birthDate: format(startOfYear(new Date()), 'yyyy-MM-dd'),
      city: '',
      country: user.country,
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName.split(' ')[0],
      phone: '',
      phoneCountryCode: '',
      postalCode: '',
      state: '',
    },
    onSubmit: (values) => {
      onSubmit({
        addressLine1: values.addressLine1 || '',
        addressLine2: values.addressLine2 || '',
        birthDate: values.birthDate ? `${values.birthDate}T02:00:00Z` : '',
        city: values.city || '',
        country: values.country || '',
        firstName: values.firstName || '',
        lastName: values.lastName || '',
        phone: values.phone || '',
        phoneCountryCode: values.phoneCountryCode || '',
        postalCode: values.postalCode || '',
        state: values.state || '',
      });
    },
  });

  const {
    errors,
    handleBlur,
    handleChange,
    handleSubmit,
    setFieldTouched,
    isSubmitting,
    setFieldValue,
    setValues,
    setSubmitting,
    touched,
    values,
    setTouched,
    submitForm,
  } = formik;

  useEffect(() => {
    setSubmitting(submitting);
  }, [setSubmitting, submitting]);

  // phoneCountryCode && phone
  const onChangeCallback = (fieldValues) => {
    setValues((prev) => ({ ...prev, ...fieldValues }));
  };

  const onModalOpen = () => {
    if (Object.keys(errors).length > 0) {
      window.scrollTo(0, 0);
      setTouched(getTouchedObj(errors));
      return false;
    }
    return true;
  };

  const handleStateChange = ({ value }) => {
    setFieldValue('state', value);
  };

  const { data: statesData, loading: statesLoading } = useQuery(StatesQuery, {
    variables: { countryId: values.country },
    skip: !values.country,
  });

  const isInvalid = (key) => !!((touched[key] && errors[key]) || serverErrors[key]);
  const stateOptions = statesData?.states?.map((state) => ({
    label: t(state.translationTag, state.name),
    value: state.id,
  }));
  const selectedStateOption = stateOptions?.find((option) => option.value === values.state);

  return (
    <form id={id} method="POST" noValidate onSubmit={handleSubmit} className="mb-3">
      <div className="form-group">
        <label htmlFor="firstName" className="d-block">
          <span className="text--sm font-weight--bold text--gray-3">
            {t('SIGNUP:CONFIRMATION_LABEL_BUSINESS_NAME')}
          </span>
        </label>
        <Input
          id="firstName"
          invalid={isInvalid('firstName')}
          name="firstName"
          onBlur={handleBlur}
          onChange={handleChange}
          type="text"
          value={values.firstName}
          disabled
        />
        {touched.firstName && errors.firstName && (
          <Input.HelperText invalid>{errors.firstName}</Input.HelperText>
        )}
        {serverErrors.firstName?.map((error) => (
          <Input.HelperText invalid key={error}>
            {error}
          </Input.HelperText>
        ))}
      </div>

      <div className="form-group">
        <label htmlFor="lastName" className="d-block">
          <span className="text--sm font-weight--bold text--gray-3">
            {t('SIGNUP:CONFIRMATION_LABEL_ENTITY_NAME')}
          </span>
        </label>
        <Input
          id="lastName"
          invalid={isInvalid('lastName')}
          name="lastName"
          onBlur={handleBlur}
          onChange={handleChange}
          type="text"
          value={values.lastName}
          disabled
        />
        {touched.lastName && errors.lastName && (
          <Input.HelperText invalid>{errors.lastName}</Input.HelperText>
        )}
        {serverErrors.lastName?.map((error) => (
          <Input.HelperText invalid key={error}>
            {error}
          </Input.HelperText>
        ))}
      </div>

      <div className="form-group">
        <label htmlFor="addressLine1" className="d-block">
          <span className="text--sm font-weight--bold text--gray-4">{`${t(
            'SIGNUP:CONFIRMATION_LABEL_ADDRESS_LINE_1',
          )}`}</span>
        </label>
        <Input
          id="addressLine1"
          invalid={isInvalid('addressLine1')}
          name="addressLine1"
          onBlur={handleBlur}
          onChange={handleChange}
          type="text"
          value={values.addressLine1}
        />
        {touched.addressLine1 && errors.addressLine1 && (
          <Input.HelperText invalid>{errors.addressLine1}</Input.HelperText>
        )}
        {serverErrors.addressLine1?.map((error) => (
          <Input.HelperText invalid key={error}>
            {error}
          </Input.HelperText>
        ))}
      </div>

      <div className="form-group">
        <label htmlFor="addressLine2" className="d-block">
          <span className="text--sm font-weight--bold text--gray-4">
            {t('SIGNUP:CONFIRMATION_LABEL_ADDRESS_LINE_2')}
          </span>
        </label>
        <Input
          id="addressLine2"
          invalid={isInvalid('addressLine2')}
          name="addressLine2"
          onBlur={handleBlur}
          onChange={handleChange}
          type="text"
          value={values.addressLine2}
        />
        {touched.addressLine2 && errors.addressLine2 && (
          <Input.HelperText invalid>{errors.addressLine2}</Input.HelperText>
        )}
        {serverErrors.addressLine2?.map((error) => (
          <Input.HelperText invalid key={error}>
            {error}
          </Input.HelperText>
        ))}
      </div>

      <div className="form-group">
        <label htmlFor="city" className="d-block">
          <span className="text--sm font-weight--bold text--gray-4">
            {t('SIGNUP:CONFIRMATION_LABEL_CITY')}
          </span>
        </label>
        <Input
          id="city"
          invalid={isInvalid('city')}
          name="city"
          onBlur={handleBlur}
          onChange={handleChange}
          placeholder={t('SIGNUP:CONFIRMATION_LABEL_CITY')}
          type="text"
          value={values.city}
        />
        {touched.city && errors.city && <Input.HelperText invalid>{errors.city}</Input.HelperText>}
        {serverErrors.city?.map((error) => (
          <Input.HelperText invalid key={error}>
            {error}
          </Input.HelperText>
        ))}
      </div>

      <div className="form-group">
        <SelectCountry
          onChange={({ value }) => {
            setFieldValue('country', value);
            setFieldValue('state', '');
          }}
          value={values.country}
          onBlur={() => setFieldTouched('country', true)}
          placeholder={t('SIGNUP:CHOOSE_COUNTRY')}
          invalid={isInvalid('country')}
        />
        {touched.country && errors.country && (
          <Input.HelperText invalid>{errors.country}</Input.HelperText>
        )}
        {serverErrors.country?.map((error) => (
          <Input.HelperText invalid key={error}>
            {error}
          </Input.HelperText>
        ))}
      </div>

      <div className="row">
        <div className="form-group col-sm-6 mb-sm-0">
          {!statesLoading && stateOptions?.length ? (
            <SelectText
              id="state"
              name="state"
              onChange={handleStateChange}
              options={stateOptions}
              placeholder={t('SIGNUP:CONFIRMATION_LABEL_STATE')}
              value={selectedStateOption}
              invalid={isInvalid('state')}
            />
          ) : (
            <Input
              id="state"
              invalid={isInvalid('state')}
              name="state"
              onBlur={handleBlur}
              onChange={handleChange}
              placeholder={t('SIGNUP:CONFIRMATION_LABEL_STATE')}
              type="text"
              value={statesLoading ? '' : values.state}
            />
          )}
          {touched.state && errors.state && (
            <Input.HelperText invalid>{errors.state}</Input.HelperText>
          )}
          {serverErrors.state?.map((error) => (
            <Input.HelperText invalid key={error}>
              {error}
            </Input.HelperText>
          ))}
        </div>
        <div className="form-group col-sm-6 ">
          <Input
            id="postalCode"
            invalid={isInvalid('postalCode')}
            name="postalCode"
            onBlur={handleBlur}
            onChange={handleChange}
            placeholder={t('SIGNUP:CONFIRMATION_LABEL_POSTAL_CODE')}
            type="text"
            value={values.postalCode}
          />
          {touched.postalCode && errors.postalCode && (
            <Input.HelperText invalid>{errors.postalCode}</Input.HelperText>
          )}
          {serverErrors.postalCode?.map((error) => (
            <Input.HelperText invalid key={error}>
              {error}
            </Input.HelperText>
          ))}
        </div>
      </div>

      <div className="form-group">
        <SelectCountryPhoneCode
          country={values.country}
          countryCodePlaceholder={t('SIGNUP:CHOOSE_COUNTRY_PHONE_CODE')}
          label={t('SIGNUP:CONFIRMATION_LABEL_PHONE')}
          onBlurPhoneNumber={handleBlur}
          onChangeCountryCode={({ value, phoneRegex }) => {
            setValues((prevValues) => ({
              ...prevValues,
              phoneCountryCode: value,
              countryPhoneRegex: phoneRegex,
            }));
          }}
          onChangePhoneNumber={handleChange}
          phoneNumber={values.phone}
          invalid={
            !!(
              (touched.phone && errors.phone) ||
              serverErrors.phone ||
              (touched.phoneCountryCode && errors.phoneCountryCode) ||
              serverErrors.phoneCountryCode
            )
          }
        />
        {touched.phone && errors.phone && (
          <Input.HelperText invalid>{errors.phone}</Input.HelperText>
        )}
        {touched.phoneCountryCode && errors.phoneCountryCode && (
          <Input.HelperText invalid>{errors.phoneCountryCode}</Input.HelperText>
        )}
        {serverErrors.phone?.map((error) => (
          <Input.HelperText invalid key={error}>
            {t(error, { name: t('SIGNUP:CONFIRMATION_LABEL_PHONE') })}
          </Input.HelperText>
        ))}
        {serverErrors.phoneCountryCode?.map((error) => (
          <Input.HelperText invalid key={error}>
            {t(error)}
          </Input.HelperText>
        ))}
      </div>
      <div className="form-group">
        <SelectBirthDate
          label={t('SIGNUP:CONFIRMATION_LABEL_ESTABLISHED_DATE')}
          values={values}
          setFieldValue={setFieldValue}
          setTouched={setTouched}
          invalid={isInvalid('birthDate')}
        />
        {touched.birthDate && errors.birthDate && (
          <Input.HelperText invalid>{errors.birthDate}</Input.HelperText>
        )}
        {serverErrors.birthDate?.map((error) => (
          <Input.HelperText invalid key={error}>
            {error}
          </Input.HelperText>
        ))}
      </div>

      <div className="text-center">
        <PhoneVerification
          handleTouchVerification={() => {}}
          onVerifyAccount={submitForm}
          onModalOpen={onModalOpen}
          formCountry={values.country}
          formPhone={values.phone}
          formPhoneCountryCode={values.phoneCountryCode}
          formCountryPhoneRegex={values.countryPhoneRegex}
          onChangeCallback={onChangeCallback}
          closeOnFinish
          isSubmitting={isSubmitting}
          buttonText={t('SIGNUP:CONFIRMATION_BUTTON_PROFILE')}
          modalTitle={t('SIGNUP:PHONE_MODAL_TITLE')}
          disableRefetchOnVerifyPhone
        />
      </div>
    </form>
  );
}

CompleteProfileForm.propTypes = {
  id: PropTypes.string.isRequired,
  submitting: PropTypes.bool.isRequired,
  user: PropTypes.shape({
    email: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    country: PropTypes.string,
  }).isRequired,
  onSubmit: PropTypes.func.isRequired,
  serverErrors: PropTypes.object,
};

CompleteProfileForm.defaultProps = {
  serverErrors: {},
};

export default CompleteProfileForm;
